import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CrmCampoDefinicaoOpcaoDto } from '@shared/services/responses/vendas/crm-configuracao-etapa-kanban.response';

export interface EncerrarOportunidadeData {
  titulo: string;
  cliente: string;
  etapaNome: string;
  /** Opções dos campos de sistema Resultado e Motivo da etapa Encerrado, configuradas pela empresa. */
  resultados: CrmCampoDefinicaoOpcaoDto[];
  motivos: CrmCampoDefinicaoOpcaoDto[];
}

export interface EncerrarOportunidadeResult {
  /** Rótulo, para as mensagens da tela. */
  resultado: string;
  motivo: string;
  /** Id da opção (campo_definicao_opcao), que é o que a API grava. */
  idCampoDefinicaoOpcaoResultado: number | null;
  idCampoDefinicaoOpcaoMotivo: number | null;
  observacoes: string;
}

/**
 * Modal obrigatório ao mover o card para a etapa final (CA15): sem Resultado e Motivo
 * preenchidos a movimentação não é confirmada.
 */
@Component({
  selector: 'app-encerrar-oportunidade-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
  ],
  template: `
    <h2 mat-dialog-title class="dialog-title">Encerrar oportunidade</h2>

    <mat-dialog-content class="dialog-content">
      <p class="dialog-text">
        Informe o desfecho de <b>{{ data.titulo }}</b> — {{ data.cliente }}. O card será movido
        para <b>{{ data.etapaNome }}</b> e o encerramento fica registrado no histórico.
      </p>

      <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
        <mat-label>Resultado</mat-label>
        <mat-select [(ngModel)]="idResultado" required>
          @for (opcao of data.resultados; track opcao.idCampoDefinicaoOpcao) {
            <mat-option [value]="opcao.idCampoDefinicaoOpcao">{{ opcao.descricao }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
        <mat-label>Motivo</mat-label>
        <mat-select [(ngModel)]="idMotivo" required>
          @for (opcao of data.motivos; track opcao.idCampoDefinicaoOpcao) {
            <mat-option [value]="opcao.idCampoDefinicaoOpcao">{{ opcao.descricao }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
        <mat-label>Observações finais</mat-label>
        <textarea matInput rows="3" [(ngModel)]="observacoes"
          placeholder="Detalhes do encerramento (opcional)…"></textarea>
      </mat-form-field>
    </mat-dialog-content>

    <mat-dialog-actions align="end" class="dialog-actions">
      <button mat-stroked-button type="button" class="btn-secondary" (click)="cancelar()">Cancelar</button>
      <button mat-flat-button type="button" class="btn-primary" [disabled]="!valida" (click)="confirmar()">
        <mat-icon class="material-symbols-outlined">check</mat-icon>
        Confirmar encerramento
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-title { margin: 0; padding: 20px 24px 12px; }
    .dialog-content {
      width: 460px; max-width: 92vw; padding: 8px 24px 12px;
      display: flex; flex-direction: column; gap: 16px;
    }
    .dialog-text { margin: 0; font-size: 13.5px; color: #3c4043; line-height: 1.5; }
    .dialog-actions { gap: 8px; margin: 0; padding: 16px 24px 20px; border-top: 1px solid #e5e7eb; }
    :host-context(body.dark-mode) .dialog-text { color: #cac4d0; }
    :host-context(body.dark-mode) .dialog-actions { border-color: #49454f; }
  `],
})
export class EncerrarOportunidadeDialogComponent {
  idResultado: number | null = null;
  idMotivo: number | null = null;
  observacoes = '';

  constructor(
    public dialogRef: MatDialogRef<EncerrarOportunidadeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EncerrarOportunidadeData,
    private alertService: AlertService
  ) {
    // Sem opção em Resultado ou Motivo não há como encerrar: avisa onde cadastrar e a confirmação fica desabilitada.
    if (!data.resultados.length || !data.motivos.length) {
      this.alertService.show(
        'Aviso',
        'Cadastre as opções de Resultado e Motivo da etapa Encerrado em Configurações › Vendas › Configuração do Kanban de Oportunidades para encerrar a oportunidade.',
        AlertType.warning
      );
    }
  }

  get valida(): boolean {
    return this.idResultado != null && this.idMotivo != null;
  }

  confirmar(): void {
    if (!this.valida) return;
    this.dialogRef.close({
      resultado: this.data.resultados.find(r => r.idCampoDefinicaoOpcao === this.idResultado)?.descricao ?? '',
      motivo: this.data.motivos.find(m => m.idCampoDefinicaoOpcao === this.idMotivo)?.descricao ?? '',
      idCampoDefinicaoOpcaoResultado: this.idResultado,
      idCampoDefinicaoOpcaoMotivo: this.idMotivo,
      observacoes: this.observacoes.trim(),
    } as EncerrarOportunidadeResult);
  }

  cancelar(): void {
    this.dialogRef.close(null);
  }
}
